// File: frontend/src/pages/HistoryPage.js
// Version: 1.0 (quick-260909-v7b)
// Date: 2026-09-09
// Назначение: экран «История» арендатора fitenko — лента ВСЕЙ ФИРМЫ и лента
//             ОДНОГО ЧЕЛОВЕКА на СУЩЕСТВУЮЩЕЙ единственной форме чтения.
//
// 🔴 ДВА РУБЕЖА, И ОНИ РАЗНЫЕ.
//    (1) АРЕНДАТОР: содержимое рисуется под `isFitenko`. Маршрут общий на всех,
//        рубеж — в разметке; MLG и ELN не получают ни одного узла экрана.
//    (2) РОЛЬ: лента ФИРМЫ отдаёт события всех клиентов разом, и своего
//        ограничения у неё нет. Рубеж на сервере — `get_current_staff_user`
//        (тот же, каким закрыт сводный отчёт по всему докету фирмы). Здесь
//        стоит его ЗЕРКАЛО, и зеркало не заведено вторым списком ролей: форма
//        предиката берётся ИЗ МОДЕЛИ МЕНЮ (`LEGACY_DESTINATIONS`, ключ
//        `history`) и вычисляется общим `evalAllow`. Держатель прав один.
//    🔴 Зеркало НЕ ЗАМЕНЯЕТ сервер: адрес ручки набирается руками, и отказ
//       сервера (403) экран показывает СЛОВАМИ ОБ ОТКАЗЕ, а не пустой лентой.
//
// 🔴 НИЧЕГО НЕ ДОРИСОВАНО. Всё, что видно на карточке, пришло в ответе ручки:
//    время, тип, заголовок, текст, номер дела. Чего в ответе нет — того на
//    экране нет тоже: ни «Вопросов», ни «Решений» (писателей 0), ни имени там,
//    где имени не прислали.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import apiService, { razobratSpisokObrashcheniy } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { LEGACY_DESTINATIONS } from '../config/fitenkoNav';
import { evalAllow } from '../config/navPermissions';
import { chisloDlyaPunkta } from '../components/FitenkoRail';
import { ShapkaEkrana } from '../components/FitenkoUzor';
import { metkaTipa, neopoznannyeTipy } from '../config/historyEvents';
import KartochkaSobytiya from '../components/history/KartochkaSobytiya';

// Форма предиката берётся из модели меню — второго списка ролей здесь нет.
const DOSTUP_ISTORII = (LEGACY_DESTINATIONS.find((d) => d.key === 'history') || {}).allow;

// 🔴 АДРЕС ГЛАВНОГО ДЕЙСТВИЯ ТОЖЕ БЕРЁТСЯ ИЗ МОДЕЛИ МЕНЮ, а не вписывается
//    строкой: второй держатель адреса разошёлся бы с первым молча, и ссылка
//    привела бы в никуда, ничего об этом не сказав. Ключа нет в модели —
//    действие НЕ РИСУЕТСЯ вовсе (см. ниже): ссылка в никуда хуже отсутствия.
// 🔴 НАЗНАЧЕНИЕ БЕРЁТСЯ ЦЕЛИКОМ, А НЕ ОДНИМ ПОЛЕМ (правка 11.09.2026): из него
//    нужны ТРИ вещи — адрес, ПОДПИСЬ и ФОРМА ПРАВА. Все три в модели меню уже
//    есть, и вписывать их сюда строками значило бы завести три вторых мнения.
const PUNKT_KONSILIUMA = LEGACY_DESTINATIONS.find((d) => d.key === 'ai_paralegal') || {};
const KUDA_SPROSIT = PUNKT_KONSILIUMA.path;

// 🔴 «НАЗАД К РАБОТЕ» И «ВСЯ ФИРМА» — РАЗНЫЕ ВЕЩИ С ПОХОЖИМ ВИДОМ, и путать их
//    нельзя. Первая УВОДИТ С ЭКРАНА на рабочий стол. Вторая остаётся здесь же и
//    лишь снимает отбор по одному человеку. Обе названы, чтобы следующий
//    читатель не решил, что одна из них лишняя.
const KUDA_NAZAD = (LEGACY_DESTINATIONS.find((d) => d.key === 'dashboard') || {}).path || '/dashboard';

const DEN_MS = 24 * 60 * 60 * 1000;

// Заголовок дня. «Сегодня» пишется ТОЛЬКО если это действительно сегодня:
// подпись, пережившая полночь, — ложное утверждение на экране адвоката.
const zagolovokDnya = (kluchDnya) => {
    const d = new Date(`${kluchDnya}T12:00:00`);
    if (Number.isNaN(d.getTime())) return kluchDnya;
    const polnaya = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
    const seichas = new Date();
    const segodnya = new Date(seichas.getFullYear(), seichas.getMonth(), seichas.getDate());
    const raznica = Math.round((segodnya.getTime()
        - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / DEN_MS);
    if (raznica === 0) return `Сегодня, ${polnaya}`;
    if (raznica === 1) return `Вчера, ${polnaya}`;
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
};

const kluchDnyaSobytiya = (iso) => {
    const d = new Date(iso);
    if (!iso || Number.isNaN(d.getTime())) return null;
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// Порядок дней НЕ ПЕРЕСОРТИРОВЫВАЕТСЯ: он такой, каким его отдала ручка.
const poDnyam = (sobytiya) => {
    const dni = [];
    const poKlyuchu = new Map();
    (sobytiya || []).forEach((e) => {
        const k = kluchDnyaSobytiya(e.created_at) || 'bez-daty';
        if (!poKlyuchu.has(k)) {
            poKlyuchu.set(k, { kluch: k, sobytiya: [] });
            dni.push(poKlyuchu.get(k));
        }
        poKlyuchu.get(k).sobytiya.push(e);
    });
    return dni;
};

const HistoryPage = () => {
    const { isFitenko } = useBranding();
    const { user } = useAuth();
    const [parametry, postavitParametry] = useSearchParams();
    const vybrannyj = parametry.get('lead');

    const [lidy, postavitLidy] = useState(null);
    // 🔴 ТРЕТЬЕ СОСТОЯНИЕ СПИСКА ИМЁН. `null` — «ещё не спросили», массив —
    //    «спросили и вот ответ», `bedaLidov` — «спросили и ИЗМЕРИТЕЛЬ ОТКАЗАЛ».
    //    Без третьего отказ был бы неотличим от «обращений нет», а имена в
    //    подписях ленты исчезли бы МОЛЧА.
    const [bedaLidov, postavitBeduLidov] = useState(null);
    const [lentaOtvet, postavitLentu] = useState(null);
    const [gruzim, postavitGruzim] = useState(true);
    const [beda, postavitBedu] = useState(null);      // {rod: 'otkaz'|'sboj', tekst}
    const [stroka, postavitStroku] = useState('');
    const [tabletka, postavitTabletku] = useState(null);

    // Право видеть экран считается ОБЩИМ вычислителем по форме из модели меню.
    const puskayut = useMemo(() => {
        if (!DOSTUP_ISTORII) return false;
        try {
            return evalAllow(user, DOSTUP_ISTORII);
        } catch (e) {
            return false;   // посторонняя форма — ОТКАЗ, а не молчаливое «да»
        }
    }, [user]);

    // 🔴 УСЛОВИЕ РИСОВАНИЯ ДЕЙСТВИЯ ШАПКИ ИСПРАВЛЕНО 11.09.2026 (решение Р6).
    //    До этого дня оно спрашивало «есть ли КЛЮЧ в модели меню», а надо было
    //    «есть ли у человека ПРАВО»: `tab:ai_paralegal` у PARALEGAL и ATTORNEY
    //    ложно (`navPermissions.isAllowedByKey`, последняя строка), то есть
    //    ссылка вела в ОТКАЗ ровно у тех ролей, которые ленту видят. Право
    //    считается ТЕМ ЖЕ вычислителем и по форме ИЗ МОДЕЛИ, что и право на сам
    //    экран, — второго списка ролей здесь нет.
    const puskayutVKonsilium = useMemo(() => {
        if (!KUDA_SPROSIT || !PUNKT_KONSILIUMA.allow) return false;
        try {
            return evalAllow(user, PUNKT_KONSILIUMA.allow);
        } catch (e) {
            return false;   // посторонняя форма — ОТКАЗ, а не молчаливое «да»
        }
    }, [user]);

    const zagruzit = useCallback(async () => {
        postavitGruzim(true);
        postavitBedu(null);
        try {
            const otvet = vybrannyj
                ? await apiService.getLeadHistory(vybrannyj)
                : await apiService.getFirmHistory();
            postavitLentu(otvet);
        } catch (e) {
            // 🔴 ОТКАЗ И СБОЙ — РАЗНЫЕ БЕДЫ, и они говорят разными словами.
            //    403 значит «не положено»; всё прочее — «не смогли спросить».
            //    Слить их значило бы сказать человеку неправду о причине.
            postavitBedu({ rod: e && e.status === 403 ? 'otkaz' : 'sboj',
                           tekst: (e && e.message) || 'причина не названа' });
            postavitLentu(null);
        } finally {
            postavitGruzim(false);
        }
    }, [vybrannyj]);

    useEffect(() => {
        if (!isFitenko || !puskayut) { postavitGruzim(false); return; }
        zagruzit();
    }, [isFitenko, puskayut, zagruzit]);

    useEffect(() => {
        if (!isFitenko || !puskayut) return;
        let zhiv = true;
        // 🔴 Доводы НЕ ПЕРЕДАЮТСЯ НАРОЧНО: умолчание ручки (skip=0, limit=100)
        //    ровно то же, что было до правки. Расширить охват поиска со ста имён
        //    до пятисот — ВИДИМОЕ изменение поведения экрана истории, и решает
        //    его владелец, а не уборка договора ответа.
        apiService.getLeads()
            .then((r) => {
                if (!zhiv) return;
                const razbor = razobratSpisokObrashcheniy(r);
                if (razbor.rod === 'otkaz') {
                    // ОТКАЗ ИЗМЕРИТЕЛЯ, а не «обращений нет»: пустой список
                    // здесь означал бы «имён нет», и это была бы неправда.
                    postavitLidy(null);
                    postavitBeduLidov(razbor.tekst);
                    return;
                }
                postavitBeduLidov(null);
                postavitLidy(razbor.items);
            })
            .catch((e) => {
                if (!zhiv) return;
                postavitLidy(null);
                postavitBeduLidov((e && e.message) || 'причина не названа');
            });
        return () => { zhiv = false; };
    }, [isFitenko, puskayut]);

    useEffect(() => { postavitTabletku(null); }, [vybrannyj]);

    const sobytiya = useMemo(() => (lentaOtvet && lentaOtvet.events) || [], [lentaOtvet]);

    // Таблетка рисуется ТОЛЬКО за тип, ВСТРЕТИВШИЙСЯ в загруженной ленте.
    // Порядок — порядок первой встречи в ленте, а не мой список.
    const tipyLenty = useMemo(() => {
        const schet = new Map();
        sobytiya.forEach((e) => schet.set(e.event_type, (schet.get(e.event_type) || 0) + 1));
        return Array.from(schet.entries()).map(([tip, n]) => ({ tip, n }));
    }, [sobytiya]);

    // 🔴 ПОИСК ИДЁТ ПО ЛЮДЯМ И ПО СОДЕРЖИМОМУ ОДНОЙ СТРОКОЙ. Содержимое — это
    //    заголовок и описание события, то есть РОВНО то, что пришло в ответе.
    //    Тела письма в ленте нет НИКОГДА (`listeners.py`: «Тело (body_text) в
    //    ленту не идёт НИКОГДА»), поэтому поиск по письму — это поиск ПО ТЕМЕ,
    //    и подсказка под строкой говорит это прямо: иначе строка обещала бы
    //    больше, чем ищет.
    const poSoderzhimomu = useMemo(() => {
        const q = stroka.trim().toLowerCase();
        if (!q) return sobytiya;
        return sobytiya.filter((e) => [e.title, e.description]
            .some((v) => String(v || '').toLowerCase().includes(q)));
    }, [sobytiya, stroka]);

    const vidimye = useMemo(
        () => (tabletka ? poSoderzhimomu.filter((e) => e.event_type === tabletka) : poSoderzhimomu),
        [poSoderzhimomu, tabletka],
    );

    const chuzhieTipy = useMemo(() => neopoznannyeTipy(sobytiya), [sobytiya]);

    const najdennye = useMemo(() => {
        const q = stroka.trim().toLowerCase();
        if (!q) return (lidy || []).slice(0, 8);
        return (lidy || []).filter((l) => [l.full_name, l.phone, l.email]
            .some((v) => String(v || '').toLowerCase().includes(q))).slice(0, 8);
    }, [lidy, stroka]);

    if (!isFitenko) {
        // 🔴 РУБЕЖ АРЕНДАТОРА — ОТПРАВКОЙ ТУДА, КУДА ЭТОТ АДРЕС ВЁЛ ВЧЕРА.
        //    Первая редакция рисовала здесь короткую строку-отказ, и прибор
        //    поймал последствие: до появления маршрута адрес `/history` у MLG
        //    уходил в catch-all на `/dashboard`, а со строкой-отказом стал
        //    показывать полупустую страницу — 41 узел вместо 410. Это и есть
        //    «MLG сдвинулся», пусть и на одном адресе. Обещано было «ни на
        //    пиксель», поэтому поведение возвращено ТО ЖЕ, что и было.
        return <Navigate to="/dashboard" replace />;
    }

    if (!puskayut) {
        // 🔴 КОРЕНЬ ЗДЕСЬ ДРУГОЙ, И ЭТО НЕ КОСМЕТИКА. Общая обёртка экрана означает
        //    «экран отдан». Учётной записи без прав экран НЕ ОТДАН вовсе —
        //    отдан отказ. Один и тот же корень сделал бы эти два состояния
        //    неразличимыми снаружи, и «клиент экрана не получает» стало бы
        //    непроверяемым утверждением.
        return (
            <div className="fk-hist-noaccess">
                <div className="fk-hist-deny">
                    Лента фирмы доступна сотрудникам фирмы. Ваша учётная запись
                    прав на неё не имеет — это отказ, а не отсутствие событий.
                </div>
            </div>
        );
    }

    const imyaCheloveka = lentaOtvet && lentaOtvet.display_name;
    const kartaImyon = (lentaOtvet && lentaOtvet.lead_names) || null;
    // Карта имён СОТРУДНИКОВ — тем же приёмом, что и карта имён обращений:
    // в строке ленты имени нет, есть номер. Ключи приходят строками (JSON).
    const kartaAvtorov = (lentaOtvet && lentaOtvet.actor_names) || null;
    const vsegoStroka = chisloDlyaPunkta({ value: sobytiya.length,
                                           capped: !!(lentaOtvet && lentaOtvet.usecheno) });

    return (
        <div className="fk-page">
            {/* 🔴 УВОД С ЭКРАНА, А НЕ СНЯТИЕ ОТБОРА. Кнопка «Вся фирма» ниже
                выглядит похоже и делает ДРУГОЕ — остаётся здесь же. */}
            <Link className="fk-hist-nazad" to={KUDA_NAZAD}>← Назад к работе</Link>

            {/* 🔴 ШАПКА ЭКРАНА — ОБЩАЯ (решение владельца Р5=а, 10.09.2026).
                Своей больше нет: `fk-hist-head`/`fk-hist-h1`/`fk-hist-sub` и их
                правила УДАЛЕНЫ — мёртвое правило тоже цена. Сдвиг отступа под
                заголовком на 4 px принят осознанно: держатель узора становится
                ОДИН, и этого стоило больше, чем четыре пикселя.
                🔴 Все пять пропсов обязательны — у общей шапки нет
                необязательных частей, и заводить их «чтобы влез второй экран»
                значило бы поставить в код ветку, которую никто не исполняет. */}
            {/* 🔴 ПОДПИСЬ НАЗЫВАЕТ ТО, ЧТО ПРОИСХОДИТ ПО НАЖАТИЮ. Прежняя
                    («Спросить Katifi о неделе →») врала дважды: никакого
                    вопроса про неделю она не задаёт — `ShapkaEkrana` умеет
                    только ВЕСТИ (`FitenkoUzor.js`: `<Link to={kuda}>`), — и у
                    ролей без права вела в отказ. Спрашивать про событие умеет
                    кнопка НА КАРТОЧКЕ; шапка ведёт, и подпись это говорит.
                    Слово подписи взято из МОДЕЛИ МЕНЮ: второй держатель имени
                    разошёлся бы с пунктом меню молча. */}
            <ShapkaEkrana
                nadzagolovok="Client & Case Memory"
                zagolovok="История"
                podstroka={vybrannyj
                    ? `Звонки, письма, СМС, документы, дела, задачи, вопросы и решения одного человека${imyaCheloveka ? `: ${imyaCheloveka}` : ''}.`
                    : 'Лента всей фирмы: звонки, письма, СМС, документы, дела, задачи, вопросы и решения, новые сверху.'}
                kuda={puskayutVKonsilium ? KUDA_SPROSIT : KUDA_NAZAD}
                podpis={puskayutVKonsilium
                    ? `Открыть ${PUNKT_KONSILIUMA.label} →`
                    : '← Назад к работе'} />

            <div className="fk-hist-search">
                <Search className="fk-hist-search-icon" size={16} strokeWidth={1.5} aria-hidden="true" />
                <input
                    className="fk-hist-input"
                    type="search"
                    value={stroka}
                    placeholder="Имя, телефон, почта или слово из события"
                    onChange={(ev) => postavitStroku(ev.target.value)}
                />
                {vybrannyj ? (
                    <button type="button" className="fk-hist-back"
                            onClick={() => postavitParametry({})}>
                        Вся фирма
                    </button>
                ) : null}
            </div>

            {/* 🔴 ПОДСКАЗКА НАЗЫВАЕТ ОБА ОХВАТА И ТО, ЧЕГО В ОХВАТЕ НЕТ.
                Числа — только те, что известны: сколько обращений отдала ручка
                списка и сколько событий пришло в ленте. Предел ленты и признак
                усечения приходят С СЕРВЕРА (`predel`/`usecheno`) — своего числа
                500 здесь нет, второй держатель разошёлся бы молча.
                🔴 Про тело письма сказано ПРЯМО: его в ленте нет НИКОГДА, и
                молчание об этом было бы обещанием искать больше, чем ищем. */}
            {bedaLidov ? (
                <p className="fk-hist-trunc">
                    🔴 Имена обращений спросить НЕ УДАЛОСЬ: {bedaLidov}. Поиск по людям сейчас
                    не работает — это отказ измерителя, а не «обращений нет».
                    По событиям ленты ({sobytiya.length}) поиск идёт как обычно.
                </p>
            ) : null}

            {lidy === null && !lentaOtvet ? null : (
                <p className="fk-hist-trunc">
                    Поиск идёт по двум охватам сразу: по {lidy === null ? '—' : lidy.length} последним
                    обращениям, которые отдала ручка списка, и по {sobytiya.length} событиям ленты —
                    их заголовкам и описаниям
                    {lentaOtvet && lentaOtvet.usecheno
                        ? `. Лента УСЕЧЕНА пределом сервера (${lentaOtvet.predel}): событий в базе больше, чем показано`
                        : ''}.
                    Тело письма в ленту не попадает никогда — по письмам ищется ТЕМА,
                    а не переписка.
                </p>
            )}

            {stroka.trim() && najdennye.length > 0 ? (
                <ul className="fk-hist-people">
                    {najdennye.map((l) => (
                        <li key={l.id}>
                            <button type="button" className="fk-hist-person"
                                    onClick={() => postavitParametry({ lead: String(l.id) })}>
                                <span className="fk-hist-person-name">{l.full_name || '—'}</span>
                                <span className="fk-hist-person-meta">{l.phone || l.email || ''}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}

            {stroka.trim() && lidy !== null && najdennye.length === 0 ? (
                <p className="fk-hist-people-empty">
                    Среди загруженных обращений совпадений нет. Это про загруженные,
                    а не про всю фирму.
                </p>
            ) : null}

            {gruzim ? <div className="fk-hist-load">Загружаю ленту…</div> : null}

            {!gruzim && beda && beda.rod === 'otkaz' ? (
                <div className="fk-hist-deny">
                    Отказано в доступе к ленте: {beda.tekst}. Это отказ по правам,
                    а не пустая лента.
                </div>
            ) : null}

            {!gruzim && beda && beda.rod === 'sboj' ? (
                <div className="fk-hist-empty fk-hist-empty--sboj">
                    Лента не загрузилась: {beda.tekst}. Событий может быть сколько
                    угодно — мы их не получили, и это не ноль.
                </div>
            ) : null}

            {!gruzim && !beda && sobytiya.length === 0 ? (
                <div className="fk-hist-empty">
                    Ни одного события в ленте. Записей нет в базе — это не значит,
                    что общения не было.
                </div>
            ) : null}

            {!gruzim && !beda && sobytiya.length > 0 ? (
                <>
                    <div className="fk-hist-pills">
                        <button type="button"
                                className={`fk-hist-pill fk-hist-pill--all${tabletka === null ? ' active' : ''}`}
                                onClick={() => postavitTabletku(null)}>
                            Всё ({vsegoStroka})
                        </button>
                        {tipyLenty.map(({ tip, n }) => (
                            <button key={tip} type="button"
                                    className={`fk-hist-pill${tabletka === tip ? ' active' : ''}`}
                                    onClick={() => postavitTabletku(tip)}>
                                {metkaTipa(tip)} ({n})
                            </button>
                        ))}
                    </div>

                    {poDnyam(vidimye).map((den) => (
                        <section className="fk-hist-den" key={den.kluch}>
                            <h2 className="fk-hist-day">{zagolovokDnya(den.kluch)}</h2>
                            <ul className="fk-hist-list">
                                {den.sobytiya.map((e) => (
                                    <KartochkaSobytiya key={e.id} sobytie={e}
                                        imya={(kartaImyon && kartaImyon[String(e.lead_id)]) || imyaCheloveka || null}
                                        avtor={kartaAvtorov ? kartaAvtorov[String(e.actor_user_id)] : null} />
                                ))}
                            </ul>
                        </section>
                    ))}

                    {/* 🔴 ЧУЖОЙ ТИП НАЗЫВАЕТСЯ ВСЛУХ. Молчаливое проглатывание
                        спрятало бы расхождение кода с данными. */}
                    {chuzhieTipy.length > 0 ? (
                        <p className="fk-hist-unknown">
                            Типов без словаря: {chuzhieTipy.length} ({chuzhieTipy.join(', ')}).
                            Их карточки на месте, а источник у них не опознан.
                        </p>
                    ) : null}
                </>
            ) : null}

            {lentaOtvet && lentaOtvet.note ? (
                <p className="fk-hist-note">{lentaOtvet.note}</p>
            ) : null}
        </div>
    );
};

export default HistoryPage;
